/**
 * MLT risk windows on the diurnal charts (sign-off 02-040; decision 54, Q27: the Diurnal tool's model with its 2020
 * values, kept as they are). Ported from `mlt-calculator.js` (Diurnal inventory 02 §4.3), read for behaviour only:
 * a centred dipole from the degree-1 Gauss coefficients of epoch 2020.0 with their secular variation, the Spencer
 * (1971) sun, and the local day swept minute by minute for three windows of magnetic local time.
 *
 * Not copied (bugs 02-23 to 02-25): the date is each day's own local date in the project offset (the tool's label
 * was a day early east of UTC, and a day list across New Year took the first day's year); the sun's day angle uses
 * the year's own length (the tool's 365 days put 31 December of a leap year on 1 January's sun).
 * The location is the centroid of the project's block boundaries (the tool: of the BND loaded in its tracker).
 */
import { projectedToLatLng } from './map-geo.js';
import type { Block } from './model.js';

/** Degree-1 Gauss coefficients, epoch 2020.0 (nT), and their secular variation (nT/yr), as the tool (decision 54). */
export const MLT_DIPOLE_2020 = {
  g10: -29404.5,
  g11: -1450.7,
  h11: 4652.9,
  dg10: 10.9,
  dg11: 8.3,
  dh11: -21.7,
} as const;

export type MltWindowKey = 'preMidnight' | 'magMidnight' | 'dawn';

export interface MltWindowDef {
  readonly key: MltWindowKey;
  /** Magnetic local time, hours; `lo > hi` wraps midnight. */
  readonly lo: number;
  readonly hi: number;
  readonly label: string;
  readonly short: string;
  /** Brand token (group `diurnal`) of the shading. */
  readonly colour: 'diurnal-mlt-pre-midnight' | 'diurnal-mlt-midnight' | 'diurnal-mlt-dawn';
}

export const MLT_WINDOWS: readonly MltWindowDef[] = [
  {
    key: 'preMidnight',
    lo: 22,
    hi: 24,
    label: 'Pre-midnight onset (22:00 to 00:00 MLT)',
    short: 'Pre-midnight',
    colour: 'diurnal-mlt-pre-midnight',
  },
  {
    key: 'magMidnight',
    lo: 23.5,
    hi: 0.5,
    label: 'Magnetic midnight (23:30 to 00:30 MLT)',
    short: 'Magnetic midnight',
    colour: 'diurnal-mlt-midnight',
  },
  {
    key: 'dawn',
    lo: 3,
    hi: 6,
    label: 'Dawn sector (03:00 to 06:00 MLT)',
    short: 'Dawn sector',
    colour: 'diurnal-mlt-dawn',
  },
];

/** Under this magnetic latitude (absolute) the tool marks the site "low-lat". */
export const MLT_LOW_LATITUDE_DEG = 55;

const rad = (d: number): number => (d * Math.PI) / 180;
const deg = (r: number): number => (r * 180) / Math.PI;

interface Pole {
  readonly lat: number;
  readonly lon: number;
}

function pole(year: number): Pole {
  const d = MLT_DIPOLE_2020;
  const dt = year - 2020;
  const g10 = d.g10 + d.dg10 * dt;
  const g11 = d.g11 + d.dg11 * dt;
  const h11 = d.h11 + d.dh11 * dt;
  const b0 = Math.sqrt(g10 * g10 + g11 * g11 + h11 * h11);
  return { lat: Math.PI / 2 - Math.acos(-g10 / b0), lon: Math.atan2(-h11, -g11) };
}

function geomag(latDeg: number, lonDeg: number, p: Pole): { lat: number; lon: number } {
  const s = rad(latDeg);
  const dL = rad(lonDeg) - p.lon;
  const sinM = Math.sin(s) * Math.sin(p.lat) + Math.cos(s) * Math.cos(p.lat) * Math.cos(dL);
  const lat = deg(Math.asin(Math.max(-1, Math.min(1, sinM))));
  const lon = deg(
    Math.atan2(
      Math.cos(s) * Math.sin(dL),
      Math.sin(s) * Math.cos(p.lat) - Math.cos(s) * Math.sin(p.lat) * Math.cos(dL),
    ),
  );
  return { lat, lon };
}

/** Spencer (1971): equation of time (hours) and declination (degrees) on day `doy` of a year of `daysInYear`. */
export function spencerSun(doy: number, daysInYear: number): { eqtH: number; declDeg: number } {
  const b = ((2 * Math.PI) / daysInYear) * (doy - 1);
  const eqtMin =
    229.18 *
    (0.000075 +
      0.001868 * Math.cos(b) -
      0.032077 * Math.sin(b) -
      0.014615 * Math.cos(2 * b) -
      0.04089 * Math.sin(2 * b));
  const decl =
    0.006918 -
    0.399912 * Math.cos(b) +
    0.070257 * Math.sin(b) -
    0.006758 * Math.cos(2 * b) +
    0.000907 * Math.sin(2 * b) -
    0.002697 * Math.cos(3 * b) +
    0.00148 * Math.sin(3 * b);
  return { eqtH: eqtMin / 60, declDeg: deg(decl) };
}

function mlt(
  latDeg: number,
  lonDeg: number,
  utcH: number,
  p: Pole,
  sun: { eqtH: number; declDeg: number },
): number {
  const site = geomag(latDeg, lonDeg, p).lon;
  const sunLon = geomag(sun.declDeg, 15 * (12 - utcH - sun.eqtH), p).lon;
  let d = sunLon - site;
  d = ((((d + 180) % 360) + 360) % 360) - 180;
  return (((d / 15 + 12) % 24) + 24) % 24;
}

const inWindow = (m: number, lo: number, hi: number): boolean =>
  lo < hi ? m >= lo && m < hi : m >= lo || m < hi;

export interface MltDay {
  readonly date: string;
  readonly magLatDeg: number;
  /** Per window, the local-time bands of that day, hours from local midnight (0 to 24). */
  readonly windows: Readonly<Record<MltWindowKey, ReadonlyArray<readonly [number, number]>>>;
}

/**
 * The tool's `findMLTWindows`: the windows of one local date (`YYYY-MM-DD`) at a site, in local clock hours for the
 * offset, found minute by minute (1,441 steps). A window open at midnight gives a band at each end of the day.
 */
export function mltWindows(latDeg: number, lonDeg: number, date: string, offsetH: number): MltDay {
  const [yr, mo, dy] = date.split('-').map(Number) as [number, number, number];
  const doy = Math.round((Date.UTC(yr, mo - 1, dy) - Date.UTC(yr, 0, 0)) / 86_400_000);
  const daysInYear = Math.round((Date.UTC(yr + 1, 0, 1) - Date.UTC(yr, 0, 1)) / 86_400_000);
  const p = pole(yr + (doy - 1) / 365.25);
  const sun = spencerSun(doy, daysInYear);
  const windows = { preMidnight: [], magMidnight: [], dawn: [] } as Record<
    MltWindowKey,
    Array<[number, number]>
  >;
  const open: Partial<Record<MltWindowKey, number>> = {};
  for (let i = 0; i <= 1440; i++) {
    const localH = i / 60;
    const utcH = (((localH - offsetH) % 24) + 24) % 24;
    const m = mlt(latDeg, lonDeg, utcH, p, sun);
    for (const w of MLT_WINDOWS) {
      const inside = inWindow(m, w.lo, w.hi);
      const start = open[w.key];
      if (inside && start === undefined) open[w.key] = localH;
      else if (!inside && start !== undefined) {
        windows[w.key].push([start, localH]);
        delete open[w.key];
      }
    }
  }
  for (const w of MLT_WINDOWS) {
    const start = open[w.key];
    if (start !== undefined) windows[w.key].push([start, 24]);
  }
  return { date, magLatDeg: geomag(latDeg, lonDeg, p).lat, windows };
}

export interface MltBand {
  /** Local-time seconds (the charts' time base). */
  readonly start: number;
  readonly end: number;
  readonly key: MltWindowKey;
  readonly colour: MltWindowDef['colour'];
}

/**
 * The shaded bands of every day shown: each day by its own local date (bugs 02-23, 02-24 not copied), in local
 * seconds as the charts draw them (`dayStarts`: local midnight of each day, local ms).
 */
export function mltBands(
  site: { readonly lat: number; readonly lon: number },
  dayStarts: readonly number[],
  offsetH: number,
): { readonly bands: readonly MltBand[]; readonly days: readonly MltDay[] } {
  const bands: MltBand[] = [];
  const days: MltDay[] = [];
  for (const start of dayStarts) {
    const date = new Date(start).toISOString().slice(0, 10);
    const day = mltWindows(site.lat, site.lon, date, offsetH);
    days.push(day);
    for (const w of MLT_WINDOWS)
      for (const [a, b] of day.windows[w.key])
        if (b > a)
          bands.push({
            start: start / 1000 + a * 3600,
            end: start / 1000 + b * 3600,
            key: w.key,
            colour: w.colour,
          });
  }
  return { bands, days };
}

/** `HH:MM` of decimal hours (the tool's `fmtHrs`). */
export function mltClock(h: number): string {
  const n = ((h % 24) + 24) % 24;
  let hr = Math.floor(n);
  let min = Math.round((n - hr) * 60);
  if (min === 60) {
    hr += 1;
    min = 0;
  }
  return `${String(hr % 24).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/**
 * The notice of the overlay (the tool's 12 s notification, kept on the screen): the dates and offset, the magnetic
 * latitude with "low latitude" under 55°, and the first day's windows.
 */
export function mltNotice(days: readonly MltDay[], offsetText: string): string[] {
  if (days.length === 0) return [];
  const first = days[0]!;
  const dates = days.length > 1 ? `${first.date} to ${days[days.length - 1]!.date}` : first.date;
  const low = Math.abs(first.magLatDeg) < MLT_LOW_LATITUDE_DEG;
  const lat = `${first.magLatDeg < 0 ? '−' : ''}${Math.abs(first.magLatDeg).toFixed(1)}°`;
  const lines = [
    `MLT risk windows, ${dates} (UTC${offsetText}): magnetic latitude ${lat}${low ? `, low latitude (under ${MLT_LOW_LATITUDE_DEG}°)` : ''}.`,
  ];
  for (const w of MLT_WINDOWS) {
    const arr = first.windows[w.key];
    if (arr.length)
      lines.push(`${w.short}: ${arr.map(([a, b]) => `${mltClock(a)} to ${mltClock(b)}`).join(', ')}`);
  }
  return lines;
}

/**
 * Where the windows are worked out: the centroid (mean of every vertex) of the project's survey block boundaries, in
 * latitude and longitude from each block's projection; null when no block has a boundary in a known projection.
 */
export function surveyCentroid(
  blocks: readonly Pick<Block, 'kind' | 'boundary' | 'epsg' | 'deleted_at'>[],
): { lat: number; lon: number } | null {
  let lat = 0;
  let lon = 0;
  let n = 0;
  for (const b of blocks) {
    if (b.deleted_at !== null || b.kind !== 'survey' || !b.boundary) continue;
    for (const [e, nn] of b.boundary) {
      const ll = projectedToLatLng(e, nn, b.epsg);
      if (!ll) continue;
      lat += ll.lat;
      lon += ll.lng;
      n++;
    }
  }
  return n > 0 ? { lat: lat / n, lon: lon / n } : null;
}
